import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  AutocompleteOption,
  Typography,
  Box,
  Chip,
  FormControl,
  FormLabel,
  Link,
} from '@mui/joy';

interface GoogleFont {
  family: string;
  category: 'serif' | 'sans-serif' | 'display' | 'handwriting' | 'monospace';
  variants?: string[];
  popularity?: number;
}

interface GoogleFontsSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

// Curated list of popular Google Fonts
const GOOGLE_FONTS: GoogleFont[] = [
  // Sans-serif fonts (most popular)
  { family: 'Roboto', category: 'sans-serif', popularity: 1 },
  { family: 'Open Sans', category: 'sans-serif', popularity: 2 },
  { family: 'Lato', category: 'sans-serif', popularity: 3 },
  { family: 'Montserrat', category: 'sans-serif', popularity: 4 },
  { family: 'Poppins', category: 'sans-serif', popularity: 5 },
  { family: 'Source Sans Pro', category: 'sans-serif', popularity: 6 },
  { family: 'Oswald', category: 'sans-serif', popularity: 7 },
  { family: 'Raleway', category: 'sans-serif', popularity: 8 },
  { family: 'Inter', category: 'sans-serif', popularity: 9 },
  { family: 'Ubuntu', category: 'sans-serif', popularity: 10 },
  { family: 'Mukta', category: 'sans-serif', popularity: 11 },
  { family: 'Hind', category: 'sans-serif', popularity: 12 },
  { family: 'Merriweather Sans', category: 'sans-serif', popularity: 13 },
  { family: 'Work Sans', category: 'sans-serif', popularity: 14 },
  { family: 'Quicksand', category: 'sans-serif', popularity: 15 },
  { family: 'Nunito', category: 'sans-serif', popularity: 16 },
  { family: 'Barlow', category: 'sans-serif', popularity: 17 },
  { family: 'Rubik', category: 'sans-serif', popularity: 18 },
  { family: 'Oxygen', category: 'sans-serif', popularity: 19 },
  { family: 'Noto Sans', category: 'sans-serif', popularity: 20 },
  
  // Serif fonts
  { family: 'Merriweather', category: 'serif', popularity: 21 },
  { family: 'Playfair Display', category: 'serif', popularity: 22 },
  { family: 'Lora', category: 'serif', popularity: 23 },
  { family: 'PT Serif', category: 'serif', popularity: 24 },
  { family: 'Libre Baskerville', category: 'serif', popularity: 25 },
  { family: 'Crimson Text', category: 'serif', popularity: 26 },
  { family: 'Cormorant Garamond', category: 'serif', popularity: 27 },
  { family: 'EB Garamond', category: 'serif', popularity: 28 },
  { family: 'Vollkorn', category: 'serif', popularity: 29 },
  { family: 'Bitter', category: 'serif', popularity: 30 },
  { family: 'Slabo 27px', category: 'serif', popularity: 31 },
  { family: 'Rokkitt', category: 'serif', popularity: 32 },
  { family: 'Domine', category: 'serif', popularity: 33 },
  { family: 'IBM Plex Serif', category: 'serif', popularity: 34 },
  { family: 'Zilla Slab', category: 'serif', popularity: 35 },
  
  // Display fonts
  { family: 'Bebas Neue', category: 'display', popularity: 36 },
  { family: 'Anton', category: 'display', popularity: 37 },
  { family: 'Righteous', category: 'display', popularity: 38 },
  { family: 'Fredoka One', category: 'display', popularity: 39 },
  { family: 'Alfa Slab One', category: 'display', popularity: 40 },
  { family: 'Staatliches', category: 'display', popularity: 41 },
  { family: 'Russo One', category: 'display', popularity: 42 },
  { family: 'Black Ops One', category: 'display', popularity: 43 },
  { family: 'Archivo Black', category: 'display', popularity: 44 },
  { family: 'Bungee', category: 'display', popularity: 45 },
  { family: 'Passion One', category: 'display', popularity: 46 },
  { family: 'Changa', category: 'display', popularity: 47 },
  { family: 'Acme', category: 'display', popularity: 48 },
  { family: 'Pathway Gothic One', category: 'display', popularity: 49 },
  { family: 'Teko', category: 'display', popularity: 50 },
  
  // Handwriting fonts
  { family: 'Dancing Script', category: 'handwriting', popularity: 51 },
  { family: 'Pacifico', category: 'handwriting', popularity: 52 },
  { family: 'Caveat', category: 'handwriting', popularity: 53 },
  { family: 'Great Vibes', category: 'handwriting', popularity: 54 },
  { family: 'Cookie', category: 'handwriting', popularity: 55 },
  { family: 'Lobster', category: 'handwriting', popularity: 56 },
  { family: 'Kalam', category: 'handwriting', popularity: 57 },
  { family: 'Permanent Marker', category: 'handwriting', popularity: 58 },
  { family: 'Architects Daughter', category: 'handwriting', popularity: 59 },
  { family: 'Indie Flower', category: 'handwriting', popularity: 60 },
  { family: 'Amatic SC', category: 'handwriting', popularity: 61 },
  { family: 'Shadows Into Light', category: 'handwriting', popularity: 62 },
  { family: 'Handlee', category: 'handwriting', popularity: 63 },
  { family: 'Sacramento', category: 'handwriting', popularity: 64 },
  { family: 'Courgette', category: 'handwriting', popularity: 65 },
  
  // Monospace fonts
  { family: 'Roboto Mono', category: 'monospace', popularity: 66 },
  { family: 'Source Code Pro', category: 'monospace', popularity: 67 },
  { family: 'JetBrains Mono', category: 'monospace', popularity: 68 },
  { family: 'Fira Code', category: 'monospace', popularity: 69 },
  { family: 'IBM Plex Mono', category: 'monospace', popularity: 70 },
  { family: 'Space Mono', category: 'monospace', popularity: 71 },
  { family: 'Inconsolata', category: 'monospace', popularity: 72 },
  { family: 'Courier Prime', category: 'monospace', popularity: 73 },
  { family: 'Ubuntu Mono', category: 'monospace', popularity: 74 },
  { family: 'Anonymous Pro', category: 'monospace', popularity: 75 },
];

const categoryColors = {
  'sans-serif': 'primary',
  'serif': 'neutral',
  'display': 'success',
  'handwriting': 'warning',
  'monospace': 'primary',
} as const;

export const GoogleFontsSelect: React.FC<GoogleFontsSelectProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Select a Google Font',
  required = false,
  error = false,
  helperText,
}) => {
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set());

  // Load font for preview
  const loadFont = (fontFamily: string) => {
    if (!loadedFonts.has(fontFamily)) {
      const link = document.createElement('link');
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/ /g, '+')}&display=swap`;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
      setLoadedFonts(prev => new Set(prev).add(fontFamily));
    }
  };

  // Load the selected font
  useEffect(() => {
    if (value) {
      loadFont(value);
    }
  }, [value]);

  return (
    <FormControl error={error}>
      {label && <FormLabel required={required}>{label}</FormLabel>}
      <Autocomplete
        value={value}
        onChange={(_, newValue) => onChange(newValue || '')}
        options={GOOGLE_FONTS.map(font => font.family)}
        placeholder={placeholder}
        getOptionLabel={(option) => option}
        filterOptions={(options, { inputValue }) => {
          const filtered = options.filter((option) =>
            option.toLowerCase().includes(inputValue.toLowerCase())
          );
          // Sort by popularity if available
          return filtered.sort((a, b) => {
            const fontA = GOOGLE_FONTS.find(f => f.family === a);
            const fontB = GOOGLE_FONTS.find(f => f.family === b);
            return (fontA?.popularity || 999) - (fontB?.popularity || 999);
          });
        }}
        renderOption={(props, option) => {
          const font = GOOGLE_FONTS.find(f => f.family === option);
          // Load font on hover for preview
          if (font) {
            loadFont(font.family);
          }
          
          return (
            <AutocompleteOption {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography
                  level="body-md"
                  sx={{ 
                    fontFamily: `"${option}", ${font?.category === 'serif' ? 'serif' : 'sans-serif'}`,
                    flex: 1
                  }}
                >
                  {option}
                </Typography>
                {font && (
                  <Chip
                    size="sm"
                    variant="soft"
                    color={categoryColors[font.category]}
                  >
                    {font.category}
                  </Chip>
                )}
              </Box>
            </AutocompleteOption>
          );
        }}
        sx={{
          '& input': value ? {
            fontFamily: `"${value}", ${GOOGLE_FONTS.find(f => f.family === value)?.category === 'serif' ? 'serif' : 'sans-serif'}`
          } : {}
        }}
      />
      {helperText && (
        <Typography level="body-sm" sx={{ mt: 0.5, color: error ? 'danger.500' : 'text.tertiary' }}>
          {helperText}
        </Typography>
      )}
      <Typography level="body-xs" sx={{ mt: 1, color: 'text.tertiary' }}>
        Can't find your font? <Link href="https://fonts.google.com" target="_blank" level="body-xs">Browse all Google Fonts</Link>
      </Typography>
    </FormControl>
  );
};